import { z } from 'zod';
import { http } from '@/lib/http';
import {
  LIST_PROJECTION_SCHEMA,
  LIST_DEFAULT_SOURCES,
  DETAIL_DEFAULT_SOURCES,
  DETAIL_PROJECTION_SCHEMA,
  prependSource,
} from '@/config/sources';
import {
  MediaSchema,
  ProductListSchema,
  ProductSchema,
  ProductSchemaResponseSchema,
  ProductCountsResponseSchema,
  AttributeValuesSchema,
  ExtendedContractsResponseSchema,
  ResolvedAiConfigSchema,
  ProductSourcesResponseSchema,
  ProductAreasResponseSchema,
  ProductVersionSchema,
  ProductVersionCountsSchema,
  ActivationBlocksResponseSchema,
  type ActivationBlocksResponse,
  type Media,
  type MediaUpdate,
  type Product,
  type ProductCreate,
  type ProductList,
  type ProductListParams,
  type ProductSchemaResponse,
  type ProductCountsResponse,
  type AttributeValues,
  type ExtendedContractsResponse,
  type ResolvedAiConfig,
  type ProductSourceEntry,
  type ProductSourcesParams,
  type ProductArea,
  type ProductUpdate,
  type ProductVersion,
  type ProductVersionCount,
} from './schemas';

// Cliente HTTP de la feature products.
// Cada handler valida la respuesta con su schema (Zod parse) — si el backend
// devuelve algo que no matchea, falla fuerte para no contaminar el estado.

// Construye `?page=&limit=` para la URL del endpoint POST.
function buildListQuery(params: ProductListParams): string {
  const search = new URLSearchParams();
  // Backend usa page/limit (1-based). Convertimos desde skip/take.
  const limit = Math.min(params.take ?? 50, 100);
  const page = Math.floor((params.skip ?? 0) / limit) + 1;
  search.set('page', String(page));
  search.set('limit', String(limit));
  return `?${search.toString()}`;
}

// Options extra que no forman parte de los params del filtro. Hoy solo
// `area` — el back acepta `area` en el body para reemplazar default+schema
// por la config del cliente/área. Ausente → seguimos mandando default+schema.
export interface ListProductsOptions {
  area?: string;
}

// Construye el body para POST /products/query a partir de los params del
// frontend. Filters viajan en `body.filters`; projection es el LIST_*.
// Exportado para que `/products/query/export` mande EXACTAMENTE el mismo body
// que el listado. Antes se duplicaba en product-imports/api.ts y la copia se
// quedo sin `type`/`default`, lo que hacia fallar el export con 400
// ZodValidationException: el DTO del back exige `type` y `area`-o-`default`.
// Si divergen las lentes, el XLSX trae otros SKUs que la tabla, en silencio.
export function buildListBody(
  params: ProductListParams,
  options: ListProductsOptions = {},
): Record<string, unknown> {
  const filters: Record<string, unknown> = {};
  if (params.search !== undefined && params.search !== '') {
    // `filters.search` (back nuevo) matchea OR sobre sku/name/commercialName/
    // npfCode/slug en parcial case-insensitive. Una sola barra cubre todos
    // los identificadores; antes usábamos `filters.name` que solo matcheaba name.
    filters.search = params.search;
  }
  if (params.categoryId !== undefined && params.categoryId !== '') {
    const n = Number(params.categoryId);
    if (Number.isFinite(n)) filters.categoryId = n;
  }
  if (params.brandId !== undefined && params.brandId !== '') {
    const n = Number(params.brandId);
    if (Number.isFinite(n)) filters.brandId = n;
  }
  if (params.sourceSlugs !== undefined && params.sourceSlugs.length > 0) {
    filters.sourceSlugs = params.sourceSlugs;
  }
  if (params.isActive !== undefined) {
    filters.isActive = params.isActive;
  }
  if (params.isWebActive !== undefined) {
    filters.isWebActive = params.isWebActive;
  }
  if (params.missingBrand !== undefined) {
    filters.missingBrand = params.missingBrand;
  }
  if (params.missingCategory !== undefined) {
    filters.missingCategory = params.missingCategory;
  }
  if (params.missingMedia !== undefined) {
    filters.missingMedia = params.missingMedia;
  }
  if (params.missingPrice !== undefined) {
    filters.missingPrice = params.missingPrice;
  }
  if (params.missingStock !== undefined) {
    filters.missingStock = params.missingStock;
  }
  if (params.missingPointers !== undefined && params.missingPointers.length > 0) {
    filters.missingPointers = params.missingPointers;
  }
  // Filtro por valor de atributo (release back 2026-10-05). Vacío no viaja:
  // el back exige al menos un valor por campo.
  if (params.attributes !== undefined && params.attributes.length > 0) {
    filters.attributes = params.attributes;
  }
  // Apagados por regla de activación. Va por el mismo body al export, así
  // que el XLSX trae el mismo conjunto que la tabla.
  if (params.blockedByActivationRule !== undefined) {
    filters.blockedByActivationRule = params.blockedByActivationRule;
  }
  if (params.from !== undefined && params.from !== '') {
    filters.from = params.from;
  }
  if (params.to !== undefined && params.to !== '') {
    filters.to = params.to;
  }
  // `mockPicture` va a nivel top del body (fuera de `filters`) según el
  // contrato del back: cuando true, si el producto no tiene media real, el
  // back rellena `media` con un sello (autecocertified.jpg o -tvs.jpg según
  // brand). Solo lo pasamos si viene explícito para no romper llamadas de
  // /pending que asumen defaults.
  const body: Record<string, unknown> = {
    type: 'exact',
    // `default` y `schema` los ignora el back cuando viene `area`, pero los
    // seguimos mandando: los backends viejos requieren `default`, y los
    // nuevos toleran ambos. Cuando el back haga que `area` reemplace el
    // requerimiento de `default`, podemos ir quitándolos.
    default: [...LIST_DEFAULT_SOURCES],
    schema: LIST_PROJECTION_SCHEMA,
    filters,
  };
  if (options.area) body.area = options.area;
  if (params.mockPicture !== undefined) {
    body.mockPicture = params.mockPicture;
  }
  return body;
}

export async function listProducts(
  params: ProductListParams,
  options: ListProductsOptions = {},
): Promise<ProductList> {
  const raw = await http.post<unknown>(
    `/products/query${buildListQuery(params)}`,
    buildListBody(params, options),
  );
  return ProductListSchema.parse(raw);
}

// GET /products/attribute-values (release back 2026-10-05) -----------------
//
// Valores que toma un atributo, para armar su filtro en el listado
// (`filters.attributes`). Permiso `products:read`.
//
// - Con `area`: cuentan solo las fuentes que el área lee, y el valor lo elige
//   su prioridad. Si el área no lee el pointer, 422.
// - Sin `area`: el valor lo elige `default`, que tiene que ser la MISMA
//   prioridad de fuentes que manda el listado en su body. Si no, la lista de
//   valores y el filtro mirarían productos consolidados distintos.
// - `isActive` / `isWebActive` acotan el conteo con el mismo criterio que los
//   filtros del listado.
export interface AttributeValuesOptions {
  area?: string;
  isActive?: boolean;
  isWebActive?: boolean;
  // Default del back 100, máximo 500.
  limit?: number;
}

export async function listAttributeValues(
  pointer: string,
  options: AttributeValuesOptions = {},
): Promise<AttributeValues> {
  const qs = new URLSearchParams({ pointer });
  if (options.area) qs.set('area', options.area);
  else qs.set('default', LIST_DEFAULT_SOURCES.join(','));
  if (options.isActive !== undefined) qs.set('isActive', String(options.isActive));
  if (options.isWebActive !== undefined) qs.set('isWebActive', String(options.isWebActive));
  if (options.limit !== undefined) qs.set('limit', String(options.limit));
  const raw = await http.get<unknown>(`/products/attribute-values?${qs.toString()}`);
  return AttributeValuesSchema.parse(raw);
}

// POST /products/query/counts (release hub-service-00167-cxd) --------------
//
// Endpoint dedicado que consolida hasta 12 counters en un solo round-trip.
// Body = `{type, default, schema, filters, counts}`; el mapa `counts` lleva
// una entry por counter, donde cada entry SOLO puede overridear los filtros
// de "missing-*" sobre el base. Passar `sku`/`search`/etc dentro de una
// entry hace que el back responda 400.
//
// El shape base (`type/default/schema/filters`) es el MISMO que consume
// POST /products/query — garantiza que la resolución de "missing" use la
// misma lente que el listado, y que los counts sean idénticos al fan-out
// viejo por construcción.

// Filtros permitidos dentro de cada entry del map `counts`. Type estricto
// (no `unknown`, no `Record<string, unknown>`) para que futuros callers no
// puedan colar filters no soportados sin fallar en compilación.
export interface CountsEntryFilters {
  missingPointers?: readonly string[];
  missingMedia?: boolean;
  missingBrand?: boolean;
  missingCategory?: boolean;
  missingName?: boolean;
  missingPrice?: boolean;
  missingStock?: boolean;
  // Simétricos del `missingPointers` (release peer): filtran SKUs que TIENEN
  // presencia en TODOS los pointers listados. Reservado para futuras cards
  // "completos" — hoy nadie los usa desde este helper.
  requirePointers?: readonly string[];
  requirePointersMode?: 'all' | 'any';
}

export interface CountProductsBaseParams {
  isWebActive?: boolean;
}

export interface CountProductsOptions {
  area?: string;
}

// Construye el body para el endpoint de counts. Reusa `type/default/schema`
// del listado. El `filters` base incluye SOLO los filtros comunes (isActive
// y opcional isWebActive) — cada entry del map `counts` lleva sus propios
// missing-*.
function buildCountsBody(
  params: CountProductsBaseParams,
  counts: Record<string, CountsEntryFilters>,
): Record<string, unknown> {
  const filters: Record<string, unknown> = {
    isActive: true,
  };
  if (params.isWebActive !== undefined) {
    filters.isWebActive = params.isWebActive;
  }
  return {
    type: 'exact',
    default: [...LIST_DEFAULT_SOURCES],
    schema: LIST_PROJECTION_SCHEMA,
    filters,
    counts,
  };
}

export async function countProducts(
  params: CountProductsBaseParams,
  counts: Record<string, CountsEntryFilters>,
  options: CountProductsOptions = {},
): Promise<ProductCountsResponse> {
  const qs = options.area ? `?area=${encodeURIComponent(options.area)}` : '';
  const raw = await http.post<unknown>(
    `/products/query/counts${qs}`,
    buildCountsBody(params, counts),
  );
  return ProductCountsResponseSchema.parse(raw);
}

// Detalle con fuente opcional via POST /products/:sku/query (estilo
// projection schema). Si el producto vive en una fuente distinta a master
// (ej: axioma), el caller pasa el slug y se prepende al `default` para que
// se priorice como fuente principal.
//
// `includePendings` (admin-only) hace que el back adjunte un sidecar
// `pendings` con las categorías/marcas/applicationModels que las fuentes
// enviaron pero aún no resolvimos contra el catálogo maestro. Se usa en el
// PIM para distinguir "sin mapear" vs "la fuente no envió el dato".
export interface GetProductOptions {
  sourceSlug?: string;
  includePendings?: boolean;
  // Slug de área: cuando viene, el back ignora `default+schema` y aplica la
  // config del cliente. También activa el mapa `editability` en el response.
  area?: string;
}

// Aviso que viaja en `meta.pagination.notice` cuando el back responde 200
// con `data: null` — hoy el único caso conocido es AREA_WITHOUT_DATA (release
// backend 2026-09-21): el área activa no lee ninguna fuente que tenga el SKU.
// `code` no se usa para ramificar (el front no conoce de antemano los codes
// futuros); se conserva para debug/telemetría.
export interface AreaWithoutDataNotice {
  code: string;
  message: string;
}

// Resultado discriminado de `getProduct`. Reemplaza el viejo `Promise<Product>`
// a propósito: un `Product | null` deja que el caller se olvide de chequear
// el null (TS no obliga); un discriminante por `kind` sí lo obliga — no hay
// forma de leer `.product` sin antes angostar por `kind === 'found'`.
export type ProductQueryResult =
  | { kind: 'found'; product: Product }
  | { kind: 'areaWithoutData'; sku: string; notice: AreaWithoutDataNotice };

// Reconoce el shape que `unwrapEnvelope` (`@/lib/http`) preserva para un
// envelope `{ data: null, error: null, meta }`: sigue el 200 normal (nunca es
// esto lo que dispara un throw), así que hay que distinguirlo a mano ANTES de
// intentar `ProductSchema.parse` — un producto real nunca tiene `data: null`
// a nivel raíz. Narrow manual (no Zod): es forma de transporte del envelope,
// no una entidad del dominio.
function extractAreaWithoutDataNotice(raw: unknown): AreaWithoutDataNotice | null {
  if (!raw || typeof raw !== 'object' || !('data' in raw)) return null;
  if ((raw as { data: unknown }).data !== null) return null;
  const meta = (raw as { meta?: unknown }).meta;
  if (!meta || typeof meta !== 'object') return null;
  const pagination = (meta as { pagination?: unknown }).pagination;
  if (!pagination || typeof pagination !== 'object') return null;
  const notice = (pagination as { notice?: unknown }).notice;
  if (!notice || typeof notice !== 'object') return null;
  const code = (notice as { code?: unknown }).code;
  const message = (notice as { message?: unknown }).message;
  if (typeof code !== 'string' || typeof message !== 'string') return null;
  return { code, message };
}

export async function getProduct(
  sku: string,
  optionsOrSlug?: string | GetProductOptions,
): Promise<ProductQueryResult> {
  const opts: GetProductOptions =
    typeof optionsOrSlug === 'string' ? { sourceSlug: optionsOrSlug } : (optionsOrSlug ?? {});
  const defaultSources = prependSource(DETAIL_DEFAULT_SOURCES, opts.sourceSlug);
  const body: Record<string, unknown> = {
    type: 'complete',
    default: defaultSources,
    // Override per-field para el detalle (ej: attributes.pricing suma vtex
    // al final porque el e-commerce sigue siendo fuente de precios).
    // Ver DETAIL_PROJECTION_SCHEMA.
    schema: DETAIL_PROJECTION_SCHEMA,
    filters: {},
    // Adjunta el sidecar `fieldSources` (pointer -> array de slugs) para que
    // el PIM muestre de qué fuente vino cada valor. Admin-only, enforced
    // server-side: un caller no-admin simplemente no recibe el campo.
    includeFieldSources: true,
  };
  if (opts.includePendings) body.includePendings = true;
  if (opts.area) body.area = opts.area;
  const raw = await http.post<unknown>(`/products/${encodeURIComponent(sku)}/query`, body);
  const notice = extractAreaWithoutDataNotice(raw);
  if (notice) {
    return { kind: 'areaWithoutData', sku, notice };
  }
  return { kind: 'found', product: ProductSchema.parse(raw) };
}

export async function createProduct(body: ProductCreate): Promise<Product> {
  const raw = await http.post<unknown>('/products', body);
  return ProductSchema.parse(raw);
}

// Helper: monta `?area=<slug>` para los writes que aceptan el parámetro.
// El back rechaza `?area=` (vacío) con 400, así que colamos el chequeo.
// `undefined` = sin param (conducta actual).
function areaQuery(area: string | undefined): string {
  if (!area) return '';
  return `?area=${encodeURIComponent(area)}`;
}

export async function updateProduct(
  sku: string,
  body: ProductUpdate,
  options: { area?: string } = {},
): Promise<Product> {
  const raw = await http.patch<unknown>(
    `/products/${encodeURIComponent(sku)}${areaQuery(options.area)}`,
    body,
  );
  return ProductSchema.parse(raw);
}

// GET /activation-rules/blocks/:sku — campos que una regla de activación
// apagó en el producto, con el motivo. Sin apagados: 200 con `blocks: []`.
// El SKU se compara tal cual en el back; uno con `/` o espacios va codificado.
export async function getActivationBlocks(sku: string): Promise<ActivationBlocksResponse> {
  const raw = await http.get<unknown>(`/activation-rules/blocks/${encodeURIComponent(sku)}`);
  return ActivationBlocksResponseSchema.parse(raw);
}

export async function deleteProduct(sku: string): Promise<void> {
  // Endpoint responde 204 sin body.
  await http.delete(`/products/${encodeURIComponent(sku)}`, {
    parseResponse: 'none',
  });
}

// Backend nuevo devuelve `{ jsonSchema, readablePointers, writablePointers,
// sourceCatalog, ... }`. El schema acepta también el shape viejo
// `{ fields, pointers }` para compat con MSW. Parse directo.
export async function getProductSchema(): Promise<ProductSchemaResponse> {
  const raw = await http.get<unknown>('/admin/products/schema');
  return ProductSchemaResponseSchema.parse(raw);
}

// Contratos tipados del catálogo de `extendedProperties` (45 keys conocidas).
// Endpoint separado del schema principal — el back deja el jsonSchema "limpio"
// y mantiene el catálogo de constraints acá.
export async function getProductExtendedContracts(): Promise<ExtendedContractsResponse> {
  const raw = await http.get<unknown>('/admin/products/schema/extended-contracts');
  return ExtendedContractsResponseSchema.parse(raw);
}

// AI configs aplicables a un producto, resueltos por jerarquía de categorías
// (exacta → ancestros → global). Si un attributeName no está en la respuesta
// es porque el back no encontró config en ningún nivel — el botón Gemini
// debe ocultarse en ese campo para evitar 422 al click.
export async function getProductResolvedAiConfigs(productId: string): Promise<ResolvedAiConfig[]> {
  const raw = await http.get<unknown>(`/products/${encodeURIComponent(productId)}/ai-configs`);
  // `unwrapEnvelope` del http client ya retorna `data` plano para envelopes
  // estándar; si por alguna razón llega el envelope crudo, lo desempacamos.
  if (Array.isArray(raw)) {
    return z.array(ResolvedAiConfigSchema).parse(raw);
  }
  if (raw && typeof raw === 'object' && 'data' in raw) {
    return z.array(ResolvedAiConfigSchema).parse((raw as { data: unknown }).data);
  }
  return z.array(ResolvedAiConfigSchema).parse(raw);
}

// Catálogo liviano de fuentes activas (slug + name) para alimentar el
// filtro multi-source. Más liviano que /admin/clients (no trae apiKey, etc).
//
// Backend acepta filtros canRead/canWrite/isActive; consumers eligen según
// caso (ej: ProductFilters quiere productores con canWrite=true).
//
// Nota: `unwrapEnvelope` detecta arrays bajo `{data, error, meta}` y los
// re-envuelve en shape paginated `{data, total, skip, take}`. Acá nos
// quedamos solo con el array — no necesitamos paginación.
export async function listProductSources(
  params: ProductSourcesParams = {},
): Promise<ProductSourceEntry[]> {
  const search = new URLSearchParams();
  if (params.canRead !== undefined) search.set('canRead', String(params.canRead));
  if (params.canWrite !== undefined) search.set('canWrite', String(params.canWrite));
  if (params.isActive !== undefined) search.set('isActive', String(params.isActive));
  const qs = search.toString();
  const raw = await http.get<unknown>(`/admin/products/sources${qs ? `?${qs}` : ''}`);
  // Shape esperado tras unwrap: { data: [...], total, skip, take }.
  // Si ya viene como array (mock o backend distinto), pasamos directo.
  const items = Array.isArray(raw)
    ? raw
    : raw &&
        typeof raw === 'object' &&
        'data' in raw &&
        Array.isArray((raw as { data: unknown }).data)
      ? (raw as { data: unknown[] }).data
      : raw;
  return ProductSourcesResponseSchema.parse(items);
}

// GET /products/areas — catálogo de áreas (clientes con fieldPriorityConfig)
// para el selector global. Cada entrada trae `slug`, `name`, `allowedFields` y `readOnlyFields`
// (pointers bloqueados por el área — semántica de prefijo).
export async function listProductAreas(): Promise<ProductArea[]> {
  const raw = await http.get<unknown>('/products/areas');
  const items = Array.isArray(raw)
    ? raw
    : raw &&
        typeof raw === 'object' &&
        'data' in raw &&
        Array.isArray((raw as { data: unknown }).data)
      ? (raw as { data: unknown[] }).data
      : raw;
  return ProductAreasResponseSchema.parse(items);
}

// Multipart media -----------------------------------------------------------

export interface ProductMediaUploadMeta {
  altText?: string;
  title?: string;
  isPrimary?: boolean;
  sortOrder?: number;
  // Tipo B: imagen master "para cuando vaya con la fuente X". Backend la
  // marca con `targetSourceSlug` y la usa cuando el cliente B2B prioriza
  // master para ese source. Sin valor → tipo C (puro admin).
  targetSourceSlug?: string;
}

function appendMediaMeta(form: FormData, meta: ProductMediaUploadMeta): void {
  if (meta.altText !== undefined) form.append('altText', meta.altText);
  if (meta.title !== undefined) form.append('title', meta.title);
  if (meta.isPrimary !== undefined) {
    form.append('isPrimary', String(meta.isPrimary));
  }
  if (meta.sortOrder !== undefined) {
    form.append('sortOrder', String(meta.sortOrder));
  }
}

export async function uploadProductMedia(
  sku: string,
  file: File,
  meta: ProductMediaUploadMeta = {},
  options: { area?: string } = {},
): Promise<Media> {
  const formData = new FormData();
  formData.append('file', file);
  appendMediaMeta(formData, meta);
  // Combinamos targetSourceSlug (media type B) + area en una sola query.
  const params = new URLSearchParams();
  if (meta.targetSourceSlug) {
    params.set('targetSourceSlug', meta.targetSourceSlug);
  }
  if (options.area) params.set('area', options.area);
  const qs = params.toString();
  // Debug de corruption UTF-8 en uploads (peer 2026-08-31: dos SKUs con
  // PNG corrompidos, bytes >= 0x80 mapeados a `EF BF BD`). Se activa con
  // `localStorage.setItem('cef:debug-upload', '1')` en la sesion; imprime
  // el sha256 del file real vs el que llega al FormData justo antes del
  // fetch. Si ambos difieren, la corruption es front-side.
  await debugAssertUploadBytes(file, formData);
  const raw = await http.post<unknown>(
    `/products/${encodeURIComponent(sku)}/media${qs ? `?${qs}` : ''}`,
    formData,
  );
  return MediaSchema.parse(raw);
}

async function debugAssertUploadBytes(file: File, formData: FormData): Promise<void> {
  try {
    if (typeof localStorage === 'undefined') return;
    if (localStorage.getItem('cef:debug-upload') !== '1') return;
  } catch {
    return;
  }
  try {
    const inFormData = formData.get('file');
    const isFile = inFormData instanceof File;
    const sizeInForm = isFile ? (inFormData as File).size : -1;
    const firstByteOriginal = new Uint8Array(await file.slice(0, 4).arrayBuffer());
    const firstByteInForm = isFile
      ? new Uint8Array(await (inFormData as File).slice(0, 4).arrayBuffer())
      : new Uint8Array();
    const digestOriginal = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
    const digestInForm = isFile
      ? await crypto.subtle.digest('SHA-256', await (inFormData as File).arrayBuffer())
      : null;
    const toHex = (buf: ArrayBuffer): string =>
      Array.from(new Uint8Array(buf))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
    // eslint-disable-next-line no-console
    console.log('[cef:debug-upload]', {
      esFile: isFile,
      tamanoOriginal: file.size,
      tamanoEnFormData: sizeInForm,
      primerBytesOriginal: Array.from(firstByteOriginal),
      primerBytesEnFormData: Array.from(firstByteInForm),
      sha256Original: toHex(digestOriginal).slice(0, 16),
      sha256EnFormData: digestInForm ? toHex(digestInForm).slice(0, 16) : null,
      mismatchSize: sizeInForm !== file.size,
      mismatchSha: digestInForm ? toHex(digestOriginal) !== toHex(digestInForm) : true,
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[cef:debug-upload] assertion failed', err);
  }
}

// Master override (tipo A): admin sube imagen propia que tapa la imagen de
// la fuente indicada (sourceMediaId). La fuente NO se borra; queda "tapada"
// cuando el cliente B2B usa master en posición 0 del mediaPriority.
export async function createMasterOverrideMedia(
  sku: string,
  sourceMediaId: string,
  file: File,
  meta: ProductMediaUploadMeta = {},
  options: { area?: string } = {},
): Promise<Media> {
  const formData = new FormData();
  formData.append('file', file);
  appendMediaMeta(formData, meta);
  const raw = await http.post<unknown>(
    `/products/${encodeURIComponent(sku)}/media/${encodeURIComponent(
      sourceMediaId,
    )}/master-override${areaQuery(options.area)}`,
    formData,
  );
  return MediaSchema.parse(raw);
}

// Borra el override master de una source-media. Idempotente: si no había
// override (no MediaLink), backend responde 204 sin cambios. Cascade borra
// row + link + GCS file.
export async function deleteMasterOverrideMedia(sku: string, sourceMediaId: string): Promise<void> {
  await http.delete(
    `/products/${encodeURIComponent(sku)}/media/${encodeURIComponent(
      sourceMediaId,
    )}/master-override`,
    { parseResponse: 'none' },
  );
}

export async function replaceProductMedia(
  sku: string,
  mediaId: string,
  file: File,
  options: { area?: string } = {},
): Promise<Media> {
  const formData = new FormData();
  formData.append('file', file);
  const raw = await http.put<unknown>(
    `/products/${encodeURIComponent(sku)}/media/${mediaId}${areaQuery(options.area)}`,
    formData,
  );
  return MediaSchema.parse(raw);
}

export async function updateProductMedia(
  sku: string,
  mediaId: string,
  body: MediaUpdate,
  options: { area?: string } = {},
): Promise<Media> {
  const raw = await http.patch<unknown>(
    `/products/${encodeURIComponent(sku)}/media/${mediaId}${areaQuery(options.area)}`,
    body,
  );
  return MediaSchema.parse(raw);
}

export async function deleteProductMedia(sku: string, mediaId: string): Promise<void> {
  await http.delete(`/products/${encodeURIComponent(sku)}/media/${mediaId}`, {
    parseResponse: 'none',
  });
}

// Revertir un master override sobre una imagen de source (release b7054cb,
// 2026-08-28). Cuando el area muestra una imagen del source pero el admin
// habia editado altText/title/isPrimary/url via `PATCH /media/:id`, el back
// creo un override sobre el source row. Este endpoint borra ese override
// sin tocar la fila del source -- la imagen vuelve a lo que el source
// publica. Aplica solo a filas cuyo `sourceUrl` viene poblado.
export async function deleteMediaMasterOverride(sku: string, sourceMediaId: string): Promise<void> {
  await http.delete(
    `/admin/products/${encodeURIComponent(sku)}/media/${sourceMediaId}/master-override`,
    { parseResponse: 'none' },
  );
}

// Oculta la imagen de una source-media para el area indicada (release
// hub-service-00157-vmp / client-gateway-00152-87b). Idempotente: si ya
// existia un text-only override para ese source, lo reusa y le prende el
// flag. Response = master row con `hiddenByAdmin: true`. Body vacio.
// Errores:
// - 422 cuando ocultar no cambia nada (el `/media` del area no prioriza
//   master, o la imagen renderizada viene de otro source). El caller debe
//   surfacear el `ApiError.message` verbatim.
// - `BUSINESS_VALIDATION` cuando el id apunta a una imagen master pura
//   (se borran con `DELETE /products/:sku/media/:id`, no acá).
export async function hideProductMedia(
  sku: string,
  sourceMediaId: string,
  options: { area?: string } = {},
): Promise<Media> {
  const raw = await http.post<unknown>(
    `/products/${encodeURIComponent(sku)}/media/${encodeURIComponent(
      sourceMediaId,
    )}/hide${areaQuery(options.area)}`,
    undefined,
  );
  return MediaSchema.parse(raw);
}

// Contraparte de `hideProductMedia`: quita el flag `hiddenByAdmin` en el
// override master (release hub-service-00157-vmp). Idempotente: unhide
// sobre algo no oculto responde 200 con la row sin flag. Body vacio.
export async function unhideProductMedia(
  sku: string,
  sourceMediaId: string,
  options: { area?: string } = {},
): Promise<Media> {
  const raw = await http.delete<unknown>(
    `/products/${encodeURIComponent(sku)}/media/${encodeURIComponent(
      sourceMediaId,
    )}/hide${areaQuery(options.area)}`,
  );
  return MediaSchema.parse(raw);
}

// Versiones del producto ---------------------------------------------------
// El backend puede devolver array crudo o envelope paginado; aceptamos ambos.

const ProductVersionsResponseSchema = z.union([
  z.array(ProductVersionSchema),
  z.object({
    data: z.array(ProductVersionSchema),
    total: z.number().int().nonnegative().optional(),
    skip: z.number().int().nonnegative().optional(),
    take: z.number().int().positive().optional(),
  }),
]);

export interface ProductVersionsQuery {
  sourceSlug?: string;
  page?: number;
  limit?: number;
}

// Página de versiones + metadata de paginación. `page`/`limit` son los que
// PEDIMOS (no los que el back pueda ecoar) — `total` es el único dato que
// realmente necesitamos leer de la respuesta; `totalPages` sale de ahí.
export interface ProductVersionsPage {
  data: ProductVersion[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

function buildVersionsQuery(query: ProductVersionsQuery): string {
  const search = new URLSearchParams();
  if (query.sourceSlug) search.set('sourceSlug', query.sourceSlug);
  if (query.page !== undefined) search.set('page', String(query.page));
  if (query.limit !== undefined) search.set('limit', String(query.limit));
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

// Versiones por SKU + fuente, paginadas. El back busca `where: { sku }`:
// mandarle el id numerico devuelve 404 "Producto", que la pantalla mostraba
// como "sin versiones". Por eso el parametro se llama `sku` y no `productId`.
//
// Sin sourceSlug el back asume master, que es la fuente con MENOS versiones
// (94.548 contra 862.995 de sap), y devuelve 404 si el producto no tiene fila
// ahi. Verificado con el back 2026-09-17.
//
// `total` sobrevive a `unwrapEnvelope` (lee `meta.pagination.total`) aunque
// éste no preserve `page`/`limit`/`totalPages` del back — por eso NO
// confiamos en ecos del back para esos dos: page/limit son los que nosotros
// pedimos, y totalPages sale de `total`/`limit`. Esto cubre también el pedido
// liviano `limit=1&page=1` que usa el selector de fuentes para leer el
// conteo total sin traer snapshots.
// Conteo de versiones POR FUENTE en un solo request. Antes esto eran N
// pedidos con `limit=1` leyendo el total del envelope — funcionaba, pero
// traía una versión entera por fuente solo para contar.
export async function listProductVersionCounts(sku: string): Promise<ProductVersionCount[]> {
  const raw = await http.get<unknown>(`/products/${encodeURIComponent(sku)}/versions/counts`);
  return ProductVersionCountsSchema.parse(raw);
}

export async function listProductVersions(
  sku: string,
  query: ProductVersionsQuery = {},
): Promise<ProductVersionsPage> {
  const raw = await http.get<unknown>(
    `/products/${encodeURIComponent(sku)}/versions${buildVersionsQuery(query)}`,
  );
  const parsed = ProductVersionsResponseSchema.parse(raw);
  const data = Array.isArray(parsed) ? parsed : parsed.data;
  // Shape raw array (mocks/MSW legacy, sin envelope): no hay `total` que
  // leer, asumimos "sin paginar" — mismo criterio que el resto del http
  // layer para endpoints sin `meta.pagination`.
  const total = Array.isArray(parsed) ? data.length : (parsed.total ?? data.length);
  const limit = query.limit ?? Math.max(data.length, 1);
  const page = query.page ?? 1;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  return { data, total, page, limit, totalPages };
}
